/**
 * useOscillationSafeWidth Hook
 *
 * Width state for measure-and-set responsive patterns where a layout effect
 * and a ResizeObserver both write the measurement back into state.
 *
 * Guards against the render→measure→setState feedback loop that crashes
 * React with error #185 ("Maximum update depth exceeded"): when switching a
 * responsive breakpoint changes the element's own measured width (e.g. the
 * taller layout toggles the canvas scrollbar on/off), the measurement
 * ping-pongs A→B→A on every commit. The guard allows one reversal (a genuine
 * resize back) and then holds the value; a stable measurement or any width
 * outside the oscillating pair re-arms it.
 */

import { useCallback, useRef, useState } from 'react';

/** Widths closer than this are treated as unchanged (sub-pixel noise). */
const EPSILON = 0.5;

export type FlipPair = { a: number; b: number; count: number };

const near = (x: number, y: number): boolean => Math.abs(x - y) <= EPSILON;

/**
 * Pure update decision — exported for unit tests.
 *
 * @param current - width currently held in state
 * @param next - freshly measured width
 * @param flip - the last correction pair being tracked (or null)
 * @returns whether to accept `next`, and the pair to track going forward
 */
export const decideWidthUpdate = (
  current: number,
  next: number,
  flip: FlipPair | null
): { accept: boolean; flip: FlipPair | null } => {
  if (near(next, current)) {
    // Stable measurement — the element has settled; forget the tracked pair
    // so genuine later resizes (undo/redo, toggles) are never held.
    return { accept: false, flip: null };
  }
  if (
    flip &&
    ((near(next, flip.a) && near(current, flip.b)) ||
      (near(next, flip.b) && near(current, flip.a)))
  ) {
    const count = flip.count + 1;
    // A second consecutive reversal of the same pair with no stable commit
    // in between is a self-sustaining oscillation, not a user resize — hold.
    return { accept: count < 2, flip: { ...flip, count } };
  }
  return { accept: true, flip: { a: current, b: next, count: 0 } };
};

/**
 * @returns [width, updateWidth] — route EVERY measurement (layout effect and
 * ResizeObserver alike) through updateWidth; it ignores sub-pixel noise and
 * breaks measurement oscillation. updateWidth has a stable identity.
 */
export const useOscillationSafeWidth = (
  initial = 0
): [number, (next: number) => void] => {
  const [width, setWidth] = useState<number>(initial);
  const widthRef = useRef<number>(initial);
  const flipRef = useRef<FlipPair | null>(null);

  const updateWidth = useCallback((next: number) => {
    const decision = decideWidthUpdate(widthRef.current, next, flipRef.current);
    flipRef.current = decision.flip;
    if (decision.accept) {
      widthRef.current = next;
      setWidth(next);
    }
  }, []);

  return [width, updateWidth];
};

export default useOscillationSafeWidth;
